import { Button, Stack } from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { RefObject, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { useReactToPrint } from "react-to-print";

import { PrinterConfiguration } from "~/api/warehouseTypes/fulfillmentCenter";
import { useAppDispatch, useAppSelector } from "~/app/store";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { usePrinter } from "~/hooks/usePrinter";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { prepareCart } from "~/redux/actions/batch";

import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectToteLabels } from "~/redux/selectors/batchSelectors";
import {
  useGetBatchQuery,
  useLazyGetBatchesQuery
} from "~/redux/warehouse/batches.hooks";
import { useGetTotesQuery } from "~/redux/warehouse/totes.hooks";

import { openAssignCartModal } from "./batch.slice";
import { toteLabelZPL } from "./toteLabelZpl";

type Props = { printComponentRef: RefObject<HTMLDivElement> };

export function BatchCartPrepToolbar({ printComponentRef }: Props) {
  const dispatch = useAppDispatch();
  const navigate = useFcNavigate();
  const { infoToast } = useToast();
  const { t } = useTranslation();
  const { batchName = "" } = useParams<{ batchName: string }>();
  const trackedPageName = useMixpanelPageName();

  const { data: batchResponse } = useGetBatchQuery({ batchId: batchName });
  const [getBatches] = useLazyGetBatchesQuery();
  const { data: totes } = useGetTotesQuery(
    batchResponse?.batch?.batchId
      ? { batchId: batchResponse.batch.batchId }
      : skipToken
  );

  const batch = batchResponse?.batch;
  const batchType = batch?.batchType;
  const cartNumberAssigned = batchResponse?.cartNumber;

  const { cartAssignmentEnabled } = useAppSelector(selectServerConfig);
  const fetchBatchFilters = useAppSelector(
    (state) => state.batch.fetchBatchFilters
  );
  const printerConfiguration = useAppSelector(
    (state) =>
      (state.store.usersFulfillmentCenter
        ?.printerConfiguration as PrinterConfiguration) || null
  );
  const toteLabels = useAppSelector((state) =>
    selectToteLabels(state, batch, totes)
  );

  const print = usePrinter();

  const handlePagePrint = useReactToPrint({
    pageStyle: `
      @page {
        margin: 0
      }
    `,
    contentRef: printComponentRef
  });

  const isCartAssigned = !!totes && totes.every((tote) => !!tote.cartId);
  const canBatchBePicked = isCartAssigned;

  const handleNextCartClicked = async () => {
    const { data } = await getBatches({
      status: ["Scheduled"],
      batchType: fetchBatchFilters.selectedBatchTypes.filter(
        (type) => type !== "Bulk" // dont include bulk batch types since theres no prep for bulk batches
      ),
      limit: 1,
      ...(fetchBatchFilters.selectedTempZones.length && {
        temperatureZone: fetchBatchFilters.selectedTempZones[0]
      }),
      ...(fetchBatchFilters.selectedOrderType && {
        orderType: fetchBatchFilters.selectedOrderType
      })
    });

    const nextBatch = data?.batches;
    if (!nextBatch?.length) {
      infoToast("No Scheduled Batches Ready");
    } else {
      navigate(`/batches/${nextBatch[0].batchName}/cart-prep`);
    }
  };

  const handleBrowserPrint = useCallback(() => {
    const labelsZPL = toteLabels?.map((label) => {
      const {
        zone,
        position = "",
        toteId,
        firstName,
        lastName = "",
        externalOrderId,
        putwallPosition,
        orderType
      } = label;
      return toteLabelZPL({
        zone,
        cartPosition: `${position}`,
        toteId,
        putwallPosition,
        customerName: `${firstName} ${lastName}`,
        externalOrderId,
        orderType
      });
    });
    if (labelsZPL) {
      print(labelsZPL.join());
    }
  }, [print, toteLabels]);

  const prepareAndPrint = useCallback(() => {
    if (batch && batch.status === "Scheduled")
      dispatch(prepareCart(batch.batchId));
    if (handlePagePrint && printerConfiguration === "PagePrint") {
      mixpanelTrack({
        trackedPageName,
        type: "Button Click",
        label: "Print"
      });
      handlePagePrint();
    } else if (printerConfiguration === "BrowserPrint") {
      mixpanelTrack({
        trackedPageName,
        type: "Button Click",
        label: "Print"
      });
      handleBrowserPrint();
    }
  }, [
    batch,
    dispatch,
    handleBrowserPrint,
    handlePagePrint,
    printerConfiguration,
    trackedPageName
  ]);

  return (
    <Stack direction="row" spacing={1}>
      {!!totes?.length &&
        (printerConfiguration === "PagePrint" ||
          printerConfiguration === "BrowserPrint") && (
          <Button color="primary" variant="contained" onClick={prepareAndPrint}>
            {t("print")}
          </Button>
        )}

      {/* only allow Assign Cart when batch is Scheduled and no cartNumber is assigned */}
      {cartAssignmentEnabled &&
        !cartNumberAssigned &&
        batch?.status === "Scheduled" && (
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              mixpanelTrack({
                trackedPageName,
                type: "Button Click",
                label: "Assign Cart"
              });
              dispatch(openAssignCartModal());
            }}
          >
            {t("assign")}
          </Button>
        )}

      {/* only show manual pick option for non-autostore batches that are ready to be picked */}
      {batchType !== "Autostore" && canBatchBePicked && (
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            if (batch) {
              mixpanelTrack({
                trackedPageName,
                type: "Button Click",
                label: "Pick Batch"
              });
              navigate(`/batches/${batch.batchName}/pick`);
            }
          }}
        >
          Pick
        </Button>
      )}
      {(batch?.status !== "Scheduled" || cartNumberAssigned !== null) && (
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            mixpanelTrack({
              trackedPageName,
              type: "Button Click",
              label: "Next Cart"
            });
            void handleNextCartClicked();
          }}
        >
          {t("next")}
        </Button>
      )}
    </Stack>
  );
}
